import * as React from "react";
import {useState} from "react";
import {LABEL_HEIGHT, niceTicks, tooltipPosition, useChartWidth, ValueAxis} from "./chartAxis";

export type BalancePoint = {
    key: string
    label: string
    isProvisional?: boolean
};

export type BalanceLine = {
    id: string
    label: string
    color: string // CSS color
    strokeWidth: number
    values: (number | null)[] // one per point, null where the balance is not known
    dashedFrom?: number // index of the first point whose segment is drawn dashed, a forecast
};

export type BalanceThreshold = {
    label: string
    amount: number
    color: string
};

type BalanceLineChartProps = {
    points: BalancePoint[]
    lines: BalanceLine[]
    threshold?: BalanceThreshold | null
    formatAmount: (amount: number) => string
    height?: number
};

const AXIS_WIDTH = 72;
const TOP_PADDING = 10;

/**
 * A line chart of balances at the end of each point, e.g. a month. Every line has a solid part
 * and, from `dashedFrom` on, a dashed part for the forecast. A horizontal line marks a threshold
 * such as the minimum balance. Hovering a point shows the values of all lines.
 */
const BalanceLineChart: React.FC<BalanceLineChartProps> = ({points, lines, threshold, formatAmount, height = 280}) => {
    const [containerRef, width] = useChartWidth();
    const [hovered, setHovered] = useState<number | null>(null);

    const allValues = lines.flatMap(line => line.values).filter((value): value is number => value !== null);
    if (threshold) {
        allValues.push(threshold.amount);
    }
    const ticks = niceTicks(Math.min(0, ...allValues), Math.max(0, ...allValues));
    const domainMinimum = ticks[0];
    const domainMaximum = ticks[ticks.length - 1];

    const plotHeight = height - LABEL_HEIGHT - TOP_PADDING;
    const plotWidth = Math.max(0, width - AXIS_WIDTH);
    const band = points.length > 0 ? plotWidth / points.length : 0;
    const x = (index: number) => AXIS_WIDTH + index * band + band / 2;
    const y = (value: number) => TOP_PADDING + plotHeight - ((value - domainMinimum) / (domainMaximum - domainMinimum || 1)) * plotHeight;

    // One path per run of known values, split at the start of the forecast
    const segments = (line: BalanceLine) => {
        const result: { d: string, dashed: boolean }[] = [];
        for (let index = 1; index < points.length; index++) {
            const from = line.values[index - 1];
            const to = line.values[index];
            if (from === null || to === null || from === undefined || to === undefined) {
                continue;
            }
            result.push({d: `M${x(index - 1)},${y(from)} L${x(index)},${y(to)}`, dashed: line.dashedFrom !== undefined && index >= line.dashedFrom});
        }
        return result;
    };

    return (
        <div>
            <div ref={containerRef} className="relative" style={{height}} onMouseLeave={() => setHovered(null)}>
                {width > 0 && (
                    <svg width={width} height={height} role="img" aria-label={lines.map(line => line.label).join(', ')}>
                        <ValueAxis ticks={ticks} axisWidth={AXIS_WIDTH} width={width} y={y} formatAmount={formatAmount}/>
                        {hovered !== null && (
                            <line x1={x(hovered)} x2={x(hovered)} y1={TOP_PADDING} y2={TOP_PADDING + plotHeight} stroke="#d1d5db" strokeWidth={1}/>
                        )}
                        {threshold && (
                            <line x1={AXIS_WIDTH} x2={width} y1={y(threshold.amount)} y2={y(threshold.amount)} stroke={threshold.color} strokeWidth={1.5} strokeDasharray="2 3"/>
                        )}
                        {lines.map(line => (
                            <g key={line.id}>
                                {segments(line).map((segment, index) => (
                                    <path key={index} d={segment.d} fill="none" stroke={line.color} strokeWidth={line.strokeWidth} strokeDasharray={segment.dashed ? '6 4' : undefined} strokeLinecap="round"/>
                                ))}
                                {line.values.map((value, index) => value === null ? null : (
                                    <circle key={index} cx={x(index)} cy={y(value)} r={line.strokeWidth + 1} fill={line.dashedFrom !== undefined && index >= line.dashedFrom ? 'white' : line.color} stroke={line.color} strokeWidth={1.5}/>
                                ))}
                            </g>
                        ))}
                        {points.map((point, index) => (
                            <g key={point.key}>
                                <text x={x(index)} y={height - 6} textAnchor="middle" className={`fill-gray-500 text-[11px] ${point.isProvisional ? 'italic' : ''}`}>{point.label}</text>
                                <rect x={AXIS_WIDTH + index * band} y={0} width={band} height={height} fill="transparent" onMouseEnter={() => setHovered(index)}/>
                            </g>
                        ))}
                    </svg>
                )}
                {hovered !== null && (
                    <div
                        className="pointer-events-none absolute top-0 z-20 min-w-52 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg"
                        style={tooltipPosition(hovered, points.length, band, AXIS_WIDTH, width)}
                    >
                        <div className="mb-1 font-medium text-gray-900">{points[hovered].label}</div>
                        {lines.filter(line => line.values[hovered] !== null && line.values[hovered] !== undefined).map(line => (
                            <div key={line.id} className="flex items-center gap-2 text-gray-700">
                                <span className="h-0.5 w-3 shrink-0" style={{backgroundColor: line.color}}/>
                                <span className="flex-1 truncate">{line.label}</span>
                                <span className="tabular-nums">{formatAmount(line.values[hovered] as number)}</span>
                            </div>
                        ))}
                        {threshold && (
                            <div className="flex items-center gap-2 text-gray-700">
                                <span className="h-0.5 w-3 shrink-0" style={{backgroundColor: threshold.color}}/>
                                <span className="flex-1 truncate">{threshold.label}</span>
                                <span className="tabular-nums">{formatAmount(threshold.amount)}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600" style={{paddingLeft: AXIS_WIDTH}}>
                {lines.map(line => (
                    <li key={line.id} className="flex items-center gap-1.5">
                        <span className="inline-block w-4" style={{borderTop: `${line.strokeWidth}px solid ${line.color}`}}/>
                        {line.label}
                    </li>
                ))}
                {threshold && (
                    <li className="flex items-center gap-1.5">
                        <span className="inline-block w-4" style={{borderTop: `2px dotted ${threshold.color}`}}/>
                        {threshold.label}
                    </li>
                )}
            </ul>
        </div>
    );
};

export default BalanceLineChart;
